/**
 * Backend Health Hook
 *
 * Checks if the backend API is reachable and healthy.
 * Used to show informative error messages instead of blank pages.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { HealthResponse, HealthStatus } from "../api/types";
import { createLogger } from "@/utils/logger";
import {
  BACKEND_HEALTH_TIMEOUT_MS,
  HEALTH_COLD_START_GRACE_ATTEMPTS,
} from "@/lib/constants";
import { getApiBaseUrl } from "@/api/baseUrl";

const logger = createLogger("BackendHealth");

const API_URL = getApiBaseUrl();

// Re-export types for consumers that import from this hook
export type { HealthResponse, HealthStatus };

/** Backend health state */
export interface BackendHealthState {
  /** Whether health check is in progress */
  isChecking: boolean;
  /**
   * Whether backend is reachable and operational.
   * - true: healthy or degraded (usable)
   * - false: unhealthy or unreachable
   * - null: not yet checked
   */
  isHealthy: boolean | null;
  /** Whether backend is still starting up (e.g., loading models) */
  isStarting: boolean;
  /** Error message if backend is unreachable */
  error: string | null;
  /** Health response details */
  health: HealthResponse | null;
  /** Manually trigger a health check */
  checkHealth: () => Promise<void>;
}

/**
 * Hook to check backend health status
 *
 * @param autoCheck - Whether to check health on mount (default: true)
 * @param retryIntervalMs - Retry interval when unhealthy (default: 3000ms)
 */
export function useBackendHealth(
  autoCheck: boolean = true,
  retryIntervalMs: number = 3000,
): BackendHealthState {
  const [isChecking, setIsChecking] = useState(autoCheck);
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);

  // Use ref for attempt count to avoid dependency cycle
  // The callback was depending on attemptCount state, which it also modified,
  // causing an infinite loop when combined with useEffect dependencies
  const attemptCountRef = useRef(0);

  // Track if initial check has been done to prevent re-running on callback change
  const hasInitialCheckRef = useRef(false);

  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    setError(null);
    attemptCountRef.current += 1;
    const currentAttempt = attemptCountRef.current;
    // First couple of attempts: a failure is far more likely a cold start /
    // rolling deploy (the instance is up but not serving yet) than a real
    // outage. Shared by BOTH the non-ok-response and thrown-error branches so a
    // 5xx and a timeout get the same grace — surface "starting", never the
    // actionable "Server unavailable" until it's clearly not just a cold start.
    const isLikelyColdStart = currentAttempt < HEALTH_COLD_START_GRACE_ATTEMPTS;
    const startTime = performance.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        BACKEND_HEALTH_TIMEOUT_MS,
      );

      const response = await fetch(`${API_URL}/health`, {
        credentials: "omit",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const durationMs = Math.round(performance.now() - startTime);

      if (response.ok) {
        const data = (await response.json()) as HealthResponse;
        setHealth(data);

        // Backend is usable if healthy, degraded, or starting
        // Only unhealthy status means it's down
        const usable =
          data.status === "healthy" ||
          data.status === "degraded" ||
          data.status === "starting";
        setIsHealthy(usable);
        setIsStarting(data.status === "starting");
        setError(null);
        attemptCountRef.current = 0; // Reset on success

        logger.debug(
          `connected in ${durationMs}ms (attempt ${currentAttempt}, status: ${data.status})`,
        );
      } else {
        // A 5xx from the load balancer on the first attempts is the cold-start
        // signal (instance up, not serving yet) — not a user-actionable
        // outage. Treat it exactly like a cold-start timeout below: show
        // "starting", keep error null, and let the 3s retry recover it
        // silently. A genuine 5xx outage persists past the grace window and
        // then surfaces as "Server unavailable" with the retry action.
        const retryableColdStart = response.status >= 500 && isLikelyColdStart;
        setIsHealthy(false);
        setIsStarting(retryableColdStart);
        setHealth(null);
        setError(retryableColdStart ? null : `HTTP ${response.status}`);
        logger.warn(
          `failed in ${durationMs}ms (attempt ${currentAttempt}, HTTP ${response.status}, coldStart: ${retryableColdStart})`,
        );
      }
    } catch (err) {
      const durationMs = Math.round(performance.now() - startTime);
      // Thrown error (timeout / network) during the grace window is a cold
      // start, not a "down" backend — see isLikelyColdStart above.
      setIsHealthy(false);
      setIsStarting(isLikelyColdStart);
      setHealth(null);

      let errorType = "unknown";
      if (err instanceof Error) {
        if (err.name === "AbortError") {
          errorType = "timeout";
          setError(isLikelyColdStart ? null : "comm.timeout");
        } else if (
          err.message.includes("NetworkError") ||
          err.message.includes("Failed to fetch")
        ) {
          errorType = "network";
          setError(isLikelyColdStart ? null : "comm.network");
        } else {
          errorType = err.message;
          setError("comm.unknown");
        }
      } else {
        setError("comm.unknown");
      }

      logger.warn(
        `${errorType} after ${durationMs}ms (attempt ${currentAttempt}, coldStart: ${isLikelyColdStart})`,
      );
    } finally {
      setIsChecking(false);
    }
  }, []); // No dependencies - uses refs instead of state for attempt tracking

  // Initial health check - runs only once on mount if autoCheck is true
  useEffect(() => {
    if (autoCheck && !hasInitialCheckRef.current) {
      hasInitialCheckRef.current = true;
      void checkHealth();
    }
  }, [autoCheck, checkHealth]);

  // Retry when unhealthy or starting (to detect when startup completes)
  useEffect(() => {
    if ((isHealthy === false || isStarting) && retryIntervalMs > 0) {
      const intervalId = setInterval(checkHealth, retryIntervalMs);
      return () => clearInterval(intervalId);
    }
  }, [isHealthy, isStarting, retryIntervalMs, checkHealth]);

  return {
    isChecking,
    isHealthy,
    isStarting,
    error,
    health,
    checkHealth,
  };
}
